import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { MenuItem, Popover, usePopover } from '../components/Popover';
import { useModules } from '../lib/modules';
import { useCurrentSession } from '../stores/session';
import { toneClass } from '../components/Tone';
import { navItemsFor, type NavItem, type NavPage } from './nav-config';
import { SECTION_TONES } from './section-tone';
import styles from './ModuleBar.module.css';

// Level 2. Rendered from a permission-filtered nav config, never a static list:
// an HR Lead sees eight items and a panelist sees one (00-MASTER-PLAN S2).

/** Counts a later slice will fill from real data. Shown, not hinted, so nobody
 *  opens a module to find out whether there is work in it. */
export type ModuleCounts = Readonly<Record<string, number | undefined>>;

export function ModuleBar({ counts }: { counts?: ModuleCounts }): ReactNode {
  const session = useCurrentSession();
  const modules = useModules();
  const items = navItemsFor(session, modules);

  // One module is nothing to switch between, and none is a strip of chrome that
  // never changes. Either way the shell collapses to two levels rather than
  // showing a bar that does nothing.
  if (items.length <= 1) return null;

  return (
    <nav className={styles.bar} aria-label="Modules">
      {items.map((item) => {
        const count = counts?.[item.id];
        if (item.pages) return <ModuleMenu key={item.id} item={item} pages={item.pages} />;
        return (
          <Link
            key={item.id}
            to={item.to}
            className={`${toneClass} ${styles.item}`}
            data-tone={SECTION_TONES[item.id]}
            activeProps={{ className: `${toneClass} ${styles.item} ${styles.itemActive}` }}
            activeOptions={{ exact: item.to === '/' }}
            aria-label={item.label}
          >
            <item.icon size={16} aria-hidden />
            <span className={styles.label}>{item.label}</span>
            {count !== undefined && count > 0 && <span className={styles.count}>{count}</span>}
          </Link>
        );
      })}
    </nav>
  );
}

function ModuleMenu({
  item,
  pages,
}: {
  readonly item: NavItem;
  readonly pages: readonly NavPage[];
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${toneClass} ${styles.item} ${isActive ? styles.itemActive : ''}`}
        data-tone={SECTION_TONES[item.id]}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={item.label}
      >
        <item.icon size={16} aria-hidden />
        <span className={styles.label}>{item.label}</span>
        <ChevronDown size={14} aria-hidden />
      </button>

      <Popover anchor={anchor} onClose={close} label={item.label}>
        {pages.map((page) => (
          <MenuItem
            key={page.to}
            isSelected={pathname === page.to}
            onClick={() => {
              close();
              void navigate({ to: page.to });
            }}
          >
            {page.label}
          </MenuItem>
        ))}
      </Popover>
    </>
  );
}
